import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { createStudyPlanCall } from "../../redux/api/studyPlan.api";
import { MATERIAL_LANGUAGES, STUDY_SUBJECTS, starterTopics } from "./data";
import StudyShell from "./StudyShell";

const GOALS = [
  { id: "exam", title: "Pass an exam", body: "A date, a name, and a plan that gets you there." },
  { id: "mastery", title: "Master the subject", body: "Go deeper than the next test." },
  { id: "general", title: "Learn as I go", body: "A steady path through the course." },
];

const LEVELS = [
  { id: "beginner", label: "Beginner", emoji: "🌱" },
  { id: "intermediate", label: "Intermediate", emoji: "📘" },
  { id: "advanced", label: "Advanced", emoji: "🎓" },
];

const MASTERY = [
  { id: "familiar", label: "Familiar", emoji: "👀" },
  { id: "practiced", label: "Practiced", emoji: "✍️" },
  { id: "confident", label: "Confident", emoji: "💪" },
];

const PREFS = [
  { id: "smart_text", label: "Smart text" },
  { id: "podcast", label: "Podcast" },
  { id: "practice", label: "Practice questions" },
];

export default function StudyPlanCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preset = params.get("subject") || "Histology";
  const [step, setStep] = useState(0);
  const [subject, setSubject] = useState(STUDY_SUBJECTS.includes(preset) ? preset : "Histology");
  const [customSubject, setCustomSubject] = useState(STUDY_SUBJECTS.includes(preset) ? "" : preset);
  const [goal, setGoal] = useState("exam");
  const [examName, setExamName] = useState("");
  const [examDate, setExamDate] = useState("");
  const [language, setLanguage] = useState("en");
  const [level, setLevel] = useState("intermediate");
  const [targetMastery, setTargetMastery] = useState("practiced");
  const [preferences, setPreferences] = useState(["smart_text", "practice"]);
  const [topics, setTopics] = useState(() => starterTopics(STUDY_SUBJECTS.includes(preset) ? preset : "Histology"));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const subjectName = (customSubject || subject).trim();

  const changeSubject = (next) => {
    setSubject(next);
    setCustomSubject("");
    setTopics(starterTopics(next));
  };

  const togglePref = (id) => {
    setPreferences((current) => {
      const has = current.includes(id);
      if (has && current.length === 1) return current;
      return has ? current.filter((item) => item !== id) : [...current, id];
    });
  };

  const canNext = useMemo(() => {
    if (step === 0) return Boolean(subjectName);
    if (step === 1) return Boolean(goal && language);
    if (step === 2) return preferences.length > 0;
    return topics.some((topic) => topic.title.trim());
  }, [goal, language, preferences.length, step, subjectName, topics]);

  const save = async () => {
    if (!topics.some((topic) => topic.title.trim())) {
      setError("Keep at least one topic.");
      return;
    }
    setSaving(true);
    setError("");
    const response = await createStudyPlanCall({
      subject: subjectName,
      goal,
      examName: examName.trim(),
      materialLanguage: language,
      examDate,
      level,
      targetMastery,
      learnWithAi: true,
      preferences,
      topics: topics
        .map((topic) => ({ title: topic.title.trim(), origin: topic.origin || "user" }))
        .filter((topic) => topic.title),
    });
    setSaving(false);
    if (response?.code === "INSUFFICIENT_CREDITS") {
      setError(response.message || "You need a study credit to start a subject.");
      return;
    }
    if (!response?.plan?.id) {
      setError(response?.message || "The study plan could not be created.");
      return;
    }
    navigate(`/study/plan/${response.plan.id}`);
  };

  return (
    <StudyShell>
      <div className="study-wizard">
        <div className="study-section" style={{ marginTop: 0 }}>
          <h2>New study plan</h2>
          <button type="button" onClick={() => navigate("/study")}>Cancel</button>
        </div>
        <div className="study-steps" aria-hidden>
          {[0, 1, 2, 3].map((index) => <span key={index} className={index <= step ? "on" : ""} />)}
        </div>

        {step === 0 ? (
          <>
            <p className="study-meta">What are you studying?</p>
            {STUDY_SUBJECTS.map((item) => (
              <button
                key={item}
                type="button"
                className={subject === item && !customSubject ? "study-choice selected" : "study-choice"}
                onClick={() => changeSubject(item)}
              >
                <strong>{item}</strong>
              </button>
            ))}
            <label className="study-meta">
              Another medical course
              <input className="study-field" value={customSubject} placeholder="Course name" onChange={(event) => setCustomSubject(event.target.value)} />
            </label>
          </>
        ) : null}

        {step === 1 ? (
          <>
            {GOALS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={goal === item.id ? "study-choice selected" : "study-choice"}
                onClick={() => setGoal(item.id)}
              >
                <strong>{item.title}</strong>
                <small>{item.body}</small>
              </button>
            ))}
            <label className="study-meta">
              Exam name
              <input className="study-field" value={examName} placeholder="Optional" onChange={(event) => setExamName(event.target.value)} />
            </label>
            <label className="study-meta">
              Exam date
              <input className="study-field" type="date" value={examDate} onChange={(event) => setExamDate(event.target.value)} />
            </label>
            <label className="study-meta">
              Lesson language
              <select className="study-field" value={language} onChange={(event) => setLanguage(event.target.value)}>
                {MATERIAL_LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </label>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="study-meta">Your level</p>
            {LEVELS.map((item) => (
              <button key={item.id} type="button" className={level === item.id ? "study-choice selected" : "study-choice"} onClick={() => setLevel(item.id)}>
                <strong>{item.emoji} {item.label}</strong>
              </button>
            ))}
            <p className="study-meta">Target</p>
            {MASTERY.map((item) => (
              <button key={item.id} type="button" className={targetMastery === item.id ? "study-choice selected" : "study-choice"} onClick={() => setTargetMastery(item.id)}>
                <strong>{item.emoji} {item.label}</strong>
              </button>
            ))}
            <p className="study-meta">Formats</p>
            {PREFS.map((item) => (
              <button key={item.id} type="button" className={preferences.includes(item.id) ? "study-choice selected" : "study-choice"} onClick={() => togglePref(item.id)}>
                <strong>{item.label}</strong>
              </button>
            ))}
          </>
        ) : null}

        {step === 3 ? (
          <>
            <p className="study-meta">Topics for {subjectName}. Edit them before the first lesson.</p>
            {topics.map((topic, index) => (
              <div className="study-topic-row" key={`${topic.title}-${index}`}>
                <input
                  value={topic.title}
                  onChange={(event) => {
                    const next = [...topics];
                    next[index] = { ...topic, title: event.target.value, origin: "user" };
                    setTopics(next);
                  }}
                />
                <button type="button" onClick={() => setTopics(topics.filter((_, item) => item !== index))}>Remove</button>
              </div>
            ))}
            <button type="button" className="study-choice" onClick={() => setTopics([...topics, { title: "", origin: "user" }])}>
              <strong>Add a topic</strong>
            </button>
          </>
        ) : null}

        {error ? <div className="study-error">{error}</div> : null}
        <div className="study-hero-actions">
          {step > 0 ? (
            <button type="button" className="study-choice" style={{ width: "auto" }} onClick={() => setStep(step - 1)}>Back</button>
          ) : null}
          {step < 3 ? (
            <button type="button" className="study-primary" disabled={!canNext} onClick={() => canNext && setStep(step + 1)} style={{ background: "#1e1b4b", color: "#fff" }}>
              Continue
            </button>
          ) : (
            <button type="button" className="study-primary" disabled={saving || !canNext} onClick={save} style={{ background: "#1e1b4b", color: "#fff" }}>
              {saving ? <CircularProgress size={16} color="inherit" /> : "Create plan"}
            </button>
          )}
        </div>
      </div>
    </StudyShell>
  );
}
